"use client";

import * as React from "react";
import { cn } from "@/lib/utils";

export interface AvatarV2Props {
  src?: string;
  alt?: string;
  name?: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  shape?: "circle" | "rounded";
  fallback?: string;
  decorative?: boolean;
  className?: string;
}

const SIZE_MAP: Record<NonNullable<AvatarV2Props["size"]>, string> = {
  xs: "h-6 w-6 text-[10px]",
  sm: "h-8 w-8 text-xs",
  md: "h-10 w-10 text-sm",
  lg: "h-12 w-12 text-base",
  xl: "h-16 w-16 text-lg",
};

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

export function AvatarV2({
  src,
  alt,
  name,
  size = "md",
  shape = "circle",
  fallback,
  decorative = false,
  className,
}: AvatarV2Props) {
  const [imgError, setImgError] = React.useState(false);
  const showImage = src && !imgError;
  const initials = fallback || (name ? getInitials(name) : "?");
  const radius =
    shape === "circle" ? "rounded-full" : "rounded-[var(--v2-radius-md)]";

  // Accessible name: explicit alt > name > fallback
  const accessibleName = alt || name || undefined;

  return (
    <span
      role={decorative ? "presentation" : "img"}
      aria-label={decorative ? undefined : accessibleName}
      aria-hidden={decorative || undefined}
      className={cn(
        "inline-flex shrink-0 items-center justify-center overflow-hidden bg-slate-100 font-semibold text-[var(--v2-text-muted)]",
        SIZE_MAP[size],
        radius,
        className
      )}
    >
      {showImage ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={src}
          alt={decorative ? "" : accessibleName || ""}
          onError={() => setImgError(true)}
          className="h-full w-full object-cover"
        />
      ) : (
        <span aria-hidden="true">{initials}</span>
      )}
    </span>
  );
}
